import { UseMutationOptions, UseQueryOptions, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { OffsetLimit } from '~/api/common'
import { SEARCH_ALL_RELATED_FIELDS, reportsV3Api } from '~/api/reportsV3'
import { queryClient } from '~/common/react-query/queryClient'
import { DEFAULT_ACTIVITY_LOG_PAGE_SIZE } from '~/components/v2/ActivityLogDrawer'
import {
  DatasetRelation,
  FieldTypes,
  FilterOptionsResponse,
  ListSystemVariablesResponse,
  ReportQuery,
  ReportScheduleResponse,
  ReportSettingsResponse,
  RunFormattingDateOptionsPayload,
  RunFormattingPreviewPayload,
  RunFormattingPreviewResponse,
  SaveSchedulePayload,
  ScheduledReportPayload
} from '~/custom_reports/types'
import { customReportsApi as api } from '../api/reports'

export const customReportsApi = {
  ...api,
  ...reportsV3Api
}

// Re-exported for existing callers; owned by ~/api/reportsV3 to avoid a require cycle (see there).
export { SEARCH_ALL_RELATED_FIELDS }

export const getUserSettingsQuery = (urlParams: any = {}) => {
  return {
    queryKey: ['report_settings', urlParams],
    queryFn: (): Promise<ReportSettingsResponse> => api.getReportSettings(urlParams),
    staleTime: 1000 * 60 * 5 // Five Minutes
  }
}

export const useReportSettings = (urlParams: any = {}) => {
  const {
    data: settings,
    isLoading,
    isError
  } = useQuery({
    ...getUserSettingsQuery(urlParams),
    select: ({ settings }) => settings
  })

  return { settings, isLoading, isError }
}

export const useReportPermissions = () => {
  const {
    data: permissions,
    isLoading,
    isError
  } = useQuery({
    ...getUserSettingsQuery(),
    select: ({ permissions }) => permissions
  })

  return { permissions, isLoading, isError }
}

export const useReportFilters = () => {
  const {
    data: filters,
    isLoading,
    isError
  } = useQuery({
    ...getUserSettingsQuery(),
    select: ({ filters }) =>
      filters.map(({ default_is_open, ...rest }) => {
        return {
          ...rest,
          defaultIsOpen: default_is_open
        }
      })
  })

  return { filters, isLoading, isError }
}

export const useGetDatasets = ({ enabled }: { enabled?: boolean }) =>
  useQuery({
    queryKey: ['report_datasets'],
    queryFn: customReportsApi.datasets.list,
    enabled,
    staleTime: Infinity
  })

export const REPORTING_GET_DATASET_RELATIONS_QUERY = 'reporting-get-dataset-relations'
export const useDatasetRelationsQuery = (datasetId: string, options?: UseQueryOptions<DatasetRelation[]>) =>
  useQuery({
    queryKey: [REPORTING_GET_DATASET_RELATIONS_QUERY, { datasetId }],
    queryFn: () => customReportsApi.datasets.getRelations(datasetId),
    staleTime: Infinity,
    ...options
  })

export const REPORTING_RUN_FORMATTING_PREVIEW_QUERY = 'reporting-run-formatting-preview'
export const useRunFormattingPreviewMutation = (
  opts: UseMutationOptions<RunFormattingPreviewResponse, unknown, RunFormattingPreviewPayload>
) =>
  useMutation({
    mutationFn: (payload: RunFormattingPreviewPayload) => customReportsApi.formats.runFormattingPreview(payload),
    ...opts,
    onSuccess: async (...args) => {
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(...args)
    }
  })

export const DATE_FORMAT_OPTIONS_QUERY = 'reporting-date-format-options'
export const useDateFormatOptionsQuery = (
  { payload }: { payload: RunFormattingDateOptionsPayload },
  { enabled = true }: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [DATE_FORMAT_OPTIONS_QUERY, { payload }],
    queryFn: async (): Promise<{ result: Array<{ id: string; name: string }> }> => {
      return {
        result: await Promise.all(
          payload.formats.map(async format => {
            return {
              id: format.config.code,
              name: (
                await customReportsApi.formats.runFormattingPreview({
                  ...payload,
                  format: { [format.id]: format.config }
                })
              ).result[0] as string
            }
          })
        )
      }
    },
    enabled,
    staleTime: Infinity
  })

export const useReportScheduleMutation = (opts: UseMutationOptions<unknown, unknown, SaveSchedulePayload>) =>
  useMutation({
    mutationFn: (payload: SaveSchedulePayload) => {
      return payload.id === undefined
        ? customReportsApi.schedules.create(payload)
        : customReportsApi.schedules.update(payload)
    },
    ...opts,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries([LIST_REPORT_SCHEDULE_QUERY])
      await queryClient.invalidateQueries([REPORT_SCHEDULE_QUERY, variables.id])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(data, variables, context)
    }
  })

export const LIST_REPORT_SCHEDULE_QUERY = 'reporting-scheduling-list-{reportId}'
export const useListReportScheduleQuery = (
  { reportId }: { reportId: string },
  { enabled = true }: { enabled?: boolean }
) =>
  useQuery({
    queryKey: [LIST_REPORT_SCHEDULE_QUERY, { reportId }],
    queryFn: () => customReportsApi.schedules.list(reportId),
    enabled
  })

export const REPORT_SCHEDULE_QUERY = 'reporting-scheduling-{reportId}-{scheduleId}'
export const useReportScheduleQuery = (
  { reportId, scheduleId }: { reportId: string; scheduleId: number },
  options?: UseQueryOptions<ReportScheduleResponse | null>
) =>
  useQuery({
    queryKey: [REPORT_SCHEDULE_QUERY, { reportId, scheduleId }],
    queryFn: () => customReportsApi.schedules.get(reportId, scheduleId),
    ...options
  })

export const useDeleteReportScheduleMutation = (opts: UseMutationOptions<unknown, unknown, ScheduledReportPayload>) =>
  useMutation({
    mutationFn: ({ scheduleId }: ScheduledReportPayload) => customReportsApi.schedules.delete(scheduleId),
    ...opts,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries([LIST_REPORT_SCHEDULE_QUERY])
      await queryClient.invalidateQueries([REPORT_SCHEDULE_QUERY, variables.scheduleId])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(data, variables, context)
    }
  })

export const useExecuteScheduledReportMutation = (opts: UseMutationOptions<unknown, unknown, ScheduledReportPayload>) =>
  useMutation({
    mutationFn: ({ scheduleId }: ScheduledReportPayload) => customReportsApi.schedules.execute(scheduleId),
    ...opts,
    onSuccess: async (data, variables, context) => {
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(data, variables, context)
    }
  })

export const useReportScheduleActivityLogQuery = (
  scheduleId: number,
  enabled = true,
  queryParams?: Partial<OffsetLimit>
) => {
  return useInfiniteQuery({
    queryKey: [REPORT_SCHEDULE_QUERY, scheduleId, 'activity-log', queryParams],
    queryFn: ({ pageParam = 0 }) =>
      customReportsApi.schedules.getActivityLogs(scheduleId, {
        limit: DEFAULT_ACTIVITY_LOG_PAGE_SIZE,
        offset: Number(pageParam)
      }),
    enabled,
    getNextPageParam: lastPage => lastPage.next_params?.offset ?? false
  })
}

export const LIST_SFTP_OPTIONS = 'reporting-list-sftp-options'
export const useListSftpOptions = ({ enabled = true }: { enabled?: boolean }) =>
  useQuery({
    queryKey: [LIST_SFTP_OPTIONS],
    queryFn: () => customReportsApi.schedules.listSftpDefinitions(),
    enabled,
    staleTime: Infinity
  })

export { useReportDetailsMutation } from '~/api/reportsV3'

export const LIST_SYSTEM_VARIABLES = 'reporting-system-variables-list'
export const useReportSystemVariables = (options?: UseQueryOptions<ListSystemVariablesResponse>) =>
  useQuery({
    queryKey: [LIST_SYSTEM_VARIABLES],
    queryFn: () => customReportsApi.reports.listSystemVariables(),
    ...options,
    cacheTime: Infinity,
    staleTime: Infinity
  })

export const LIST_ALL_RELATED_FIELDS = 'reporting-all-associated-fields-list'
export const useReportAllRelatedFields = (
  { reportQuery: { dataset, calculatedFields, parameterFields } }: { reportQuery: Partial<ReportQuery> },
  { enabled }: { enabled: boolean }
) => {
  const reportQuery = { dataset, calculatedFields, parameterFields }

  return useQuery({
    queryKey: [LIST_ALL_RELATED_FIELDS, { reportQuery }],
    queryFn: async ({ signal }) => {
      await queryClient.cancelQueries([LIST_ALL_RELATED_FIELDS])

      return customReportsApi.datasets.listAllRelatedFields(reportQuery, { signal })
    },
    enabled,
    staleTime: Infinity,
    keepPreviousData: true
  })
}

export const useReportSearchAllRelatedFields = (
  {
    reportQuery: { dataset, calculatedFields, parameterFields },
    search,
    fieldType,
    paths
  }: { reportQuery: Partial<ReportQuery>; search?: string; fieldType?: FieldTypes; paths?: string[] },
  { enabled }: { enabled: boolean }
) => {
  const reportQuery = { dataset, calculatedFields, parameterFields }

  return useQuery({
    queryKey: [SEARCH_ALL_RELATED_FIELDS, { reportQuery, search, fieldType, paths }],
    queryFn: async ({ signal }) => {
      await queryClient.cancelQueries([SEARCH_ALL_RELATED_FIELDS])

      return customReportsApi.datasets.searchRelatedFields(reportQuery, paths, search, fieldType, { signal })
    },
    enabled,
    staleTime: Infinity,
    keepPreviousData: true
  })
}

export const LIST_DATASET_FILTER_OPTIONS = 'reporting-list-dataset-filter-options-{datasetId}-{fieldId}-{omittedIds}'
export const useDatasetFilterOptions = (
  { datasetId, fieldId, omittedIds }: { datasetId: string; fieldId: string; omittedIds: Array<number> },
  queryConfig?: UseQueryOptions<FilterOptionsResponse>
) =>
  useQuery({
    queryKey: [LIST_DATASET_FILTER_OPTIONS, { datasetId, fieldId, omittedIds }],
    queryFn: () => customReportsApi.datasets.getFilterOptions(datasetId, fieldId, omittedIds),
    staleTime: Infinity,
    keepPreviousData: true,
    ...queryConfig
  })
